import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

import { findAncestors } from "shared/lib/treeUtils"
import { isProjectFloor, isProjectRoom } from "shared/types/Project"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"
import { useAggregatedProjectData } from "entities/projects/api/useAggregatedProjectData"

import { useProjectStore } from "./useProjectStore"

interface UseProjectInitialSelectionArgs {
  projectID: string
}

export function useProjectInitialSelection({
  projectID
}: UseProjectInitialSelectionArgs) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectElement } = useProjectStore(projectID)

  const data = useAggregatedProjectData({
    projectID,
    options: { includeChildren: true }
  })

  const elementID = searchParams.get("element")
  const tab = searchParams.get("tab")

  useEffect(() => {
    if (elementID && !data.project?.isLoading) {
      const ancestors = findAncestors(
        elementID,
        data.project?.data?.children,
        true
      )
      const floorAncestor = ancestors?.find(isProjectFloor)
      const roomAncestor = ancestors?.find(isProjectRoom)

      let tabID = UNASSIGNED_PRODUCTS_TAB_ID
      if (roomAncestor) {
        tabID = UNASSIGNED_ROOMS_TAB_ID
      }
      if (floorAncestor) {
        tabID = floorAncestor.id
      }

      if (tab === tabID) {
        selectElement(elementID, { openDetailsView: true })
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev)
            next.delete("element")

            return next
          },
          { replace: true }
        )
      } else {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev)

            next.set("tab", tabID)

            return next
          },
          { replace: true }
        )
      }
    }
  }, [elementID, tab, data.project?.isLoading])
}
